/* eslint-disable max-lines */
/* eslint-disable max-len */
import { Button, Tooltip } from '@nazka/nazka.mapframe.components'
import React, { useEffect, useState } from 'react'

import './blue-green.styl'
import { faArrowUp, faCircleDot, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { contents, features } from './blue-green.constants'

function Modal({ onClose, img }) {
  return (
    <div className="blue-green-modal" onClick={onClose}>
      <div className="blue-green-modal__close" onClick={e => e.stopPropagation()}>
        <Button
          icon={faXmark}
          onClick={onClose}
        />
      </div>
      <img
        src={img}
        alt="modal img"
        className="blue-green-modal__img"
        onClick={e => e.stopPropagation()}
      />
    </div>
  )
}

function BlueGreen() {
  const [isVisible, setIsVisible] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [modalImg, setModalImg] = useState('')

  const handleClick = (id) => {
    const element = document.querySelector(`#${id}`);
    if (element) { 
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  const handleReturn = () => {
    const element = document.querySelector('#header');
    element.scrollIntoView({ behavior: 'smooth' })
  }
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(false)
          } else {
            setIsVisible(true)
          }
        });
      }, 
    );
    const element = document.getElementById('header')
    observer.observe(element)
  }, []);

  return (
    <div className="blue-green">
      <div className="blue-green__header" id="header">
        <div className="blue-green__title">
          <h2>Why blue-green infrastructure for climate change adaptation?</h2>
          <p>
            Our cities are challenged by the 
            {' '}
            <strong>need to adapt to the impacts of climate change.</strong>
            {' '}
            Urbanization and the reduction of urban green spaces further increase flood risk and
            eliminate valuable connections with nature. A new approach to managing urban flood risk is necessary 
            {' '}
            <strong>to make our cities more resilient to the effects of climate change.</strong>
            {' '}
            Blue-green infrastructure offers 
            {' '}
            <strong>a resilient approach to urban flood and water management.</strong>
            {' '}
            One advantage over gray infrastructure is that nature-based measures can deliver 
            {' '}
            <strong>additional benefits</strong>
            , such as increasing wildlife and biodiversity, improving water quality and energy efficiency, enhancing mental and physical health, and creating economic opportunities.
            <strong> In short, blue-green infrastructure helps us manage the impacts of climate change while enhancing the quality of life in our cities.</strong>
          </p>
        </div>
        
        <div className="blue-green__img-wrapper">
          <img
            src="/img/blue-green/blue-green.webp" 
            alt="Blue green deployment diagram" 
            draggable={false}
            width="1200"
            height="800"
            decoding="async"
          />
          {features.map(feature => (
            <Tooltip
              key={feature.id}
              id={feature.tooltipId}
              place="top"
              tooltip={feature.tooltip}
            >
              <div
                className="blue-green__single-btn"
                data-tooltip-id={feature.tooltipId}
                style={{
                  left: `${feature.x}%`,
                  top: `${feature.y}%`,
                }}
              >
                <Button
                  icon={faCircleDot}
                  iconProps={{ color: '#0367A5' }}
                  hover={false}
                  css={{
                    borderRadius: '50%',
                    backgroundColor: '#c5dce8ff',
                    border: 'none',
                  }}
                  onClick={() => handleClick(feature.id)}
                />
              </div>
            
            </Tooltip>
          ))}
  
        </div>
      </div>
      <div className="blue-green__intro__wrapper">
        <h3>How can we implement blue-green infrastructure? </h3>
        <h4>Introduction to the BGI Measures Catalogue</h4>
        <p>
          To evaluate the potential of Blue–Green Infrastructure (BGI), three implementation scenarios were developed using specific infiltration Curve Number (CN) coefficients: 
        </p>
        <span>Scenario 1 – Green Roofs:</span>
        <p>BGI implementation through green roofs installed on all existing buildings.​</p>
        <span>
          Scenario 2 – Grass Areas:
        </span>
        <p>
          BGI implementation across all grassed areas.​
          A weighted average CN is applied using all BGI measures except green roofs and permeable pavements.​
        </p>
        <span>Scenario 3 – Impermeable Pavements:</span>
        <p>
          BGI implementation on all impermeable paved surfaces.​
          A weighted average CN is applied using all BGI measures except green roofs.​
        </p>
        <span>Catalogue Overview</span>
        <p>
          In the following catalogue, you will find key technical information for each BGI measure, including:​
        </p>
        <ul>
          <li>Hydrological functions and benefits​</li>
          <li>Limitations and considerations for implementation​</li>
          <li>Maintenance requirements​</li>
          <li>Recommendations for effective application​</li>
        </ul>
      </div>
      {contents.map((content) => {
        return (
          <div className="blue-green__each-content__wrapper" id={content.id}>
            <div className="blue-green__each-content">
              <h2>{content.name}</h2>
              <div className="blue-green__each-content__img">
              
                <div className="blue-green__img-container">
                  <div className="blue-green__objective-box">
                    <h3>Objectives</h3>
                    <p>
                      {content.objectives}
                    </p>
                  </div>
                  <img
                    src={content.img}
                    alt="measures image"
                    className="blue-green__img"
                    onClick={() => {
                      setModalVisible(true);
                      setModalImg(content.largeImg)
                    }}
                  />
                  
                </div>
                
                <h3>Description</h3>
                <p>
                  {content.description}
                </p>
              
              </div>

              <h3>Hydrological functions</h3>
              <ul>
                {content.hydrological_functions.map(list => (
                  <li>{list}</li>
                ))}
              </ul>
              <h3>Advantages</h3>
              <ul>
                {content.advantages.map(list => (
                  <li>{list}</li>
                ))}
              </ul>
              <h3>Limitations</h3>
              <ul>
                {content.limitations.map(list => (
                  <li>{list}</li>
                ))}
              </ul>
              <h3>Maintenance</h3>
              <ul>
                {content.maintenance.map(list => (
                  <li>{list}</li>
                ))}
              </ul>
              <h3>Recommendations</h3>
              <ul>
                {content.recommendations.map(list => (
                  <li>{list}</li>
                ))}
              </ul>
            </div>
            {/* <div className="blue-green__each-content__img">
            <img src={content.img} alt="rainwater img" />
          </div> */}
          </div>
        );
      })}
    
      {isVisible && (
        <div className="blue-green__outlined-wrapper">
          <Button
            icon={faArrowUp}
            css={{
              borderRadius: '50%',
            }}
            onClick={() => handleReturn()}
          />
        </div>
      )}
      {modalVisible && (
        <Modal
          onClose={() => {
            setModalVisible(false)
            setModalImg(null)
          }}
          img={modalImg}
        />
      )}
    </div>
  )
}

export default BlueGreen